import React, { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import apiClient from '../services/api';

// Panel de administración (solo visible/accesible para el email admin
// configurado en el backend — ver core/deps.get_current_admin). Muestra,
// por cada cliente registrado en PCM, cuánto ha costado en Anthropic el
// uso del Importador de estados de cuenta (endpoints/statements.py),
// para que ese gasto no quede escondido dentro de la factura general del
// mes y se pueda ver de un vistazo si algún cliente importa
// desproporcionadamente más que el resto.
//
// 3 sep 2026: la tarjeta "Regalar acceso (testing)" que había vivido acá
// un rato se movió a su propia página (pages/TestersPage.js, ruta
// /testers) -- Augusto pidió explícitamente que fuera "otra sección
// dentro de ADMIN", no una tarjeta metida dentro de esta página de
// costos. Ver NotionSidebar.js, sección ADMIN, ahora con dos links.
const ClientesPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 10 ago 2026 (Augusto: "elimina a augusto@darwinoptima.com de la lista
  // de usuarios"): antes este panel era solo de lectura -- se agrega la
  // posibilidad de eliminar un cliente (y todos sus datos, ver cascade en
  // app/models/user.py) desde acá, con un diálogo de confirmación para no
  // borrar a nadie de un clic accidental. Llama a DELETE
  // /admin/clients/{id} (nuevo, ver endpoints/admin.py) -- el backend ya
  // impide que el admin se borre a sí mismo por este camino.
  const [clienteAEliminar, setClienteAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [aviso, setAviso] = useState(null); // { severity, mensaje }

  const cargarClientes = () => {
    setLoading(true);
    apiClient.get('/admin/clients')
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((err) => {
        setError(
          err.response?.status === 403
            ? 'No tienes acceso a este panel.'
            : 'No se pudo cargar la información de clientes.'
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    cargarClientes();
  }, []);

  const confirmarEliminar = async () => {
    if (!clienteAEliminar) return;
    setEliminando(true);
    try {
      await apiClient.delete(`/admin/clients/${clienteAEliminar.id}`);
      setAviso({ severity: 'success', mensaje: `Cliente ${clienteAEliminar.email} eliminado.` });
      setClienteAEliminar(null);
      cargarClientes();
    } catch (err) {
      setAviso({
        severity: 'error',
        mensaje: err.response?.data?.detail || 'No se pudo eliminar el cliente.',
      });
    } finally {
      setEliminando(false);
    }
  };

  const formatUsd = (n) => `$${(n || 0).toFixed(4)}`;
  const formatFecha = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
  const formatAntiguedad = (dias) => {
    if (dias === null || dias === undefined) return '—';
    if (dias < 1) return 'Hoy';
    if (dias < 30) return `${dias} día${dias === 1 ? '' : 's'}`;
    const meses = Math.floor(dias / 30);
    return `${meses} mes${meses === 1 ? '' : 'es'}`;
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Consumo IA Docs/Clientes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Costo de uso de Anthropic (IA) por cliente al importar estados de cuenta.
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary">Cargando...</Typography>
      )}

      {error && (
        <Box sx={{ my: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.main">{error}</Typography>
        </Box>
      )}

      {data && (
        <>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">Clientes</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {data.resumen.total_clientes}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">Pagando</Typography>
                  <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
                    {data.resumen.total_pagados}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">Documentos importados</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {data.resumen.total_documentos}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">Costo IA total</Typography>
                  <Typography variant="h5" color="error.main" sx={{ fontWeight: 'bold' }}>
                    {formatUsd(data.resumen.total_costo_ia_usd)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Clientes</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Pagó</TableCell>
                  <TableCell>Antigüedad</TableCell>
                  <TableCell align="right">Documentos importados</TableCell>
                  <TableCell align="right">Costo IA (USD)</TableCell>
                  <TableCell>Último uso</TableCell>
                  <TableCell>Registrado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.clientes.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {c.nombre || c.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{c.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={c.plan || 'free'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.pago ? 'Pagó' : 'No paga'}
                        color={c.pago ? 'success' : 'default'}
                        variant={c.pago ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{formatAntiguedad(c.antiguedad_dias)}</TableCell>
                    <TableCell align="right">{c.documentos_procesados}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', color: c.costo_ia_usd > 0 ? 'error.main' : 'text.secondary' }}
                      >
                        {formatUsd(c.costo_ia_usd)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatFecha(c.ultimo_uso)}</TableCell>
                    <TableCell>{formatFecha(c.registrado)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Eliminar cliente">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setClienteAEliminar(c)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {data.clientes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Todavía no hay clientes registrados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={!!clienteAEliminar} onClose={() => (eliminando ? null : setClienteAEliminar(null))}>
        <DialogTitle>¿Eliminar este cliente?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se eliminará permanentemente la cuenta de <strong>{clienteAEliminar?.email}</strong> y
            todos sus datos (cuentas, transacciones, deudas, presupuestos). Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClienteAEliminar(null)} disabled={eliminando}>
            Cancelar
          </Button>
          <Button onClick={confirmarEliminar} color="error" variant="contained" disabled={eliminando}>
            {eliminando ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!aviso}
        autoHideDuration={5000}
        onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {aviso ? <Alert severity={aviso.severity} onClose={() => setAviso(null)}>{aviso.mensaje}</Alert> : undefined}
      </Snackbar>
    </Container>
  );
};

export default ClientesPage;
